import { H2, H5, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { LotCardBidLabel } from '@/components/ItemCard/LotCardBidLabel';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBidActivityCount,
    getItemSummaryBidCount,
    getItemSummaryBuyNowPrice,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogStatus,
    getItemSummaryCurrency,
    getItemSummaryIsAvailable,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';

export type PriceRowSearchCardProps = {
    className?: string;
    fullWidth?: boolean;
    isCatalogCard?: boolean;
    itemId: number;
    onNavigate?: () => void;
    seeSoldPriceHover?: (isHovered: boolean) => void;
};

const itemPriceGatewayShellClassName = 'h-full tracking-[0.5px] smMin:text-sm';

const PriceRowSearchCard = ({
    className,
    fullWidth = true,
    isCatalogCard,
    itemId,
    onNavigate,
    seeSoldPriceHover = noop,
}: PriceRowSearchCardProps) => {
    const navigate = useNavigate();
    const formatMessage = useAppFormatMessage();
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const bidActivityCount = useAppSelector((state) => getItemSummaryBidActivityCount(state, itemId));
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemSummaryBuyNowPrice(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const numberOfBids = Math.max(bidCount, bidActivityCount);

    const isDone = catalogStatus === AuctionStatus.Done;
    const buyNowAvailable = buyNowStatus === 1 && isDone;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const salePriceFormatted = salePrice > 0 ? salePrice : 0;
    const currentPrice = leadingBid || salePriceFormatted || startPrice || 0;
    const currentPriceFormatted = currentPrice > 0 ? currentPrice : 0;

    let primaryReturnComponent: React.ReactNode = null;

    if (!isDone && isPaused) {
        primaryReturnComponent = (
            <div className={itemPriceGatewayShellClassName}>
                <ItemPriceGatewayContainer
                    hideIcon={isCatalogCard}
                    itemId={itemId}
                    showChildren
                >
                    <H2>{formatMessage(['lotWithdrawn'])}</H2>
                </ItemPriceGatewayContainer>
            </div>
        );
    } else if (isLotOpen) {
        primaryReturnComponent = (
            <span className="flex flex-col items-start text-text-primary">
                {!isSold && !isPassed && <LotCardBidLabel bidCount={numberOfBids} />}
                <H2>
                    <FormattedCurrency
                        convert
                        noFraction
                        sourceCurrency={currency}
                        value={currentPriceFormatted}
                    />
                </H2>
            </span>
        );
    } else if (buyNowAvailable) {
        primaryReturnComponent = (
            <span className="leading-[22px]">
                <span className="flex flex-wrap items-baseline gap-x-2xs text-text-primary">
                    <H2>
                        {formatMessage(['buy_now_for'], {
                            values: {
                                price: (
                                    <FormattedCurrency
                                        key="buy-now-for-price"
                                        sourceCurrency={currency}
                                        value={buyNowPrice}
                                    />
                                ),
                            },
                        })}
                    </H2>
                </span>
            </span>
        );
    } else if (isAvailable && !isUnsold && !isCatalogOnly) {
        primaryReturnComponent = (
            <span className="flex flex-col items-start text-text-primary">
                {!isSold && !isPassed && <LotCardBidLabel bidCount={numberOfBids} />}
                <H2>
                    <FormattedCurrency
                        convert
                        noFraction
                        sourceCurrency={currency}
                        value={currentPriceFormatted}
                    />
                </H2>
            </span>
        );
    } else {
        let returnComponentChild: React.ReactNode = null;

        if (!salePrice && isSold) {
            returnComponentChild = (
                <div className={itemPriceGatewayShellClassName}>
                    <ItemPriceGatewayContainer
                        hideIcon={isCatalogCard}
                        itemId={itemId}
                        loggedOutMessageId="price_gateway_realized_price"
                        runFunction={() => {
                            onNavigate?.();
                            navigate({ pathname: itemUrl });
                        }}
                    >
                        <H2>{formatMessage(['sold'])}</H2>
                    </ItemPriceGatewayContainer>
                </div>
            );
        } else if (Boolean(salePrice) && isSold) {
            returnComponentChild = (
                <div
                    onMouseOut={() => seeSoldPriceHover(false)}
                    onMouseOver={() => seeSoldPriceHover(true)}
                >
                    <div className={itemPriceGatewayShellClassName}>
                        <ItemPriceGatewayContainer
                            hideIcon={isCatalogCard}
                            itemId={itemId}
                            loggedOutMessageId="price_gateway_realized_price"
                            runFunction={() => {
                                onNavigate?.();
                                navigate({ pathname: itemUrl });
                            }}
                        >
                            <span className="flex flex-col items-start">
                                <LabelMini>{formatMessage(['itemBidding', 'realizedPrice'])}</LabelMini>
                                <H2>
                                    <FormattedCurrency
                                        noFraction
                                        sourceCurrency={currency}
                                        value={salePrice}
                                    />
                                </H2>
                            </span>
                        </ItemPriceGatewayContainer>
                    </div>
                </div>
            );
        } else if (isDone && isPaused) {
            returnComponentChild = (
                <div className={itemPriceGatewayShellClassName}>
                    <ItemPriceGatewayContainer
                        hideIcon={isCatalogCard}
                        itemId={itemId}
                    >
                        {isCatalogOnly ? <H2>{formatMessage(['lot_closed'])}</H2> : null}
                    </ItemPriceGatewayContainer>
                </div>
            );
        } else if (isUnsold) {
            returnComponentChild = (
                <div className={itemPriceGatewayShellClassName}>
                    <ItemPriceGatewayContainer
                        hideIcon={isCatalogCard}
                        itemId={itemId}
                    >
                        <H2>{formatMessage(['lot_unsold_pending'])}</H2>
                    </ItemPriceGatewayContainer>
                </div>
            );
        } else if (isPassed) {
            returnComponentChild = (
                <div className={itemPriceGatewayShellClassName}>
                    <ItemPriceGatewayContainer
                        hideIcon={isCatalogCard}
                        itemId={itemId}
                        showChildren
                    >
                        <H2>
                            {isCatalogOnly
                                ? formatMessage(['lot_closed'])
                                : formatMessage(['itemBidding', 'passedText'])}
                        </H2>
                    </ItemPriceGatewayContainer>
                </div>
            );
        }

        primaryReturnComponent = (
            <span className="flex flex-wrap items-baseline gap-x-2xs text-text-primary">{returnComponentChild}</span>
        );
    }

    return <H5 className={cn('mt-2xs flex', fullWidth && 'w-full', className)}>{primaryReturnComponent}</H5>;
};

export default React.memo<PriceRowSearchCardProps>(PriceRowSearchCard);
